import React, { useState, useRef, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Mail, Send, ArrowLeft, User } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { format } from "date-fns";

export default function MessagesModal({ 
  open, 
  onOpenChange, 
  messageThreads = [], 
  currentUser,
  onSendMessage,
  onLoadThreadMessages,
  onMarkThreadRead
}) {
  const [selectedThread, setSelectedThread] = useState(null);
  const [threadMessages, setThreadMessages] = useState([]);
  const [messageText, setMessageText] = useState("");
  const [loadingMessages, setLoadingMessages] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (threadMessages.length > 0) {
      scrollToBottom();
    }
  }, [threadMessages]);

  // Reset when modal closes
  useEffect(() => {
    if (!open) {
      setSelectedThread(null);
      setThreadMessages([]);
      setMessageText("");
    }
  }, [open]);

  const handleSelectThread = async (thread) => {
    setSelectedThread(thread);
    setLoadingMessages(true);
    try {
      const messages = await onLoadThreadMessages(thread.threadId);
      setThreadMessages(messages || []);
      if (thread.unreadCount > 0) {
        onMarkThreadRead(thread.threadId);
      }
    } catch (error) {
      console.error("Error loading messages:", error);
    } finally {
      setLoadingMessages(false);
    }
  };

  const handleSendMessage = async () => {
    if (!messageText.trim() || !selectedThread) return;
    
    try {
      await onSendMessage(selectedThread.otherUserId, messageText.trim());
      setMessageText("");
      // Reload thread messages
      const messages = await onLoadThreadMessages(selectedThread.threadId);
      setThreadMessages(messages || []);
    } catch (error) {
      console.error("Error sending message:", error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#1a1a1a] border border-[#2a2a2a] w-[95vw] max-w-md max-h-[85vh] md:max-h-[80vh] overflow-hidden flex flex-col mx-auto">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="text-[#d0d0d0] flex items-center gap-2">
            {selectedThread ? (
              <>
                <button 
                  onClick={() => setSelectedThread(null)}
                  className="hover:bg-[#2a2a2a] p-1 rounded-lg transition-colors"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
                <span>{selectedThread.otherUsername}</span>
              </>
            ) : (
              <>
                <Mail className="w-5 h-5" />
                Messages
              </>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex flex-col">
          {!selectedThread ? (
            // Thread list
            <div className="flex-1 overflow-y-auto space-y-2 pr-2">
              {messageThreads.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <Mail className="w-12 h-12 text-[#404040] mb-4" />
                  <p className="text-sm text-[#707070]">No messages yet</p>
                </div>
              ) : (
                messageThreads.map((thread) => (
                  <button
                    key={thread.threadId}
                    onClick={() => handleSelectThread(thread)}
                    className={`w-full text-left rounded-xl p-3 transition-all hover:bg-[#2a2a2a] ${
                      thread.unreadCount > 0
                        ? "bg-[#252525] border border-[#3a3a3a]"
                        : "bg-[#1a1a1a]"
                    }`}
                  >
                    <div className="flex gap-3 items-center">
                      <div className="w-10 h-10 rounded-full bg-[#2a2a2a] flex items-center justify-center flex-shrink-0">
                        {thread.otherAvatar ? (
                          <img
                            src={thread.otherAvatar}
                            alt=""
                            className="w-full h-full rounded-full object-cover"
                          />
                        ) : (
                          <User className="w-5 h-5 text-[#606060]" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between items-center">
                          <p className="text-sm font-medium text-[#d0d0d0] truncate">
                            {thread.otherUsername}
                          </p>
                          {thread.unreadCount > 0 && (
                            <span className="w-5 h-5 rounded-full bg-[#505050] text-[#d0d0d0] text-xs flex items-center justify-center">
                              {thread.unreadCount}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-[#808080] truncate">
                          {thread.lastMessage}
                        </p>
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>
          ) : (
            // Message thread view
            <>
              <div className="flex-1 overflow-y-auto space-y-3 pr-2 py-2">
                {loadingMessages ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="w-6 h-6 border-2 border-[#505050] border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : threadMessages.length === 0 ? (
                  <p className="text-center text-[#707070] text-sm py-8">
                    No messages in this conversation
                  </p>
                ) : (
                  threadMessages.map((msg) => {
                    const isOwn = msg.sender_id === currentUser?.id;
                    let msgTime = "";
                    try {
                      msgTime = format(new Date(msg.created_at), "h:mm a");
                    } catch {
                      msgTime = "";
                    }
                    return (
                      <div
                        key={msg.id}
                        className={`flex ${isOwn ? "justify-end" : "justify-start"}`}
                      >
                        <div
                          className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                            isOwn
                              ? "bg-[#3a3a3a] text-[#d0d0d0]"
                              : "bg-[#252525] text-[#d0d0d0]"
                          }`}
                        >
                          <p className="text-sm break-words">{msg.content}</p>
                          <p className="text-xs text-[#707070] mt-1">{msgTime}</p>
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Message input */}
              <div className="flex-shrink-0 pt-3 border-t border-[#2a2a2a]">
                <div className="flex gap-2">
                  <Textarea
                    value={messageText}
                    onChange={(e) => setMessageText(e.target.value)}
                    placeholder="Type a message..."
                    className="flex-1 bg-[#252525] border-[#3a3a3a] text-[#d0d0d0] placeholder:text-[#606060] resize-none min-h-[44px] max-h-[100px]"
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                  />
                  <button
                    onClick={handleSendMessage}
                    disabled={!messageText.trim()}
                    className="px-4 rounded-xl bg-[#2a2a2a] hover:bg-[#3a3a3a] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <Send className="w-5 h-5 text-[#a0a0a0]" />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
